import React, { useState, useRef } from 'react'

const API = process.env.REACT_APP_API_URL || 'https://sunshoos-v2.onrender.com'

const BOX_META = [
  { key: 'box1', label: 'Box 1', desc: 'VAT due on sales and other outputs' },
  { key: 'box2', label: 'Box 2', desc: 'VAT due on EC acquisitions' },
  { key: 'box3', label: 'Box 3', desc: 'Total VAT due (Box 1 + Box 2)', derived: true, highlight: 'blue' },
  { key: 'box4', label: 'Box 4', desc: 'VAT reclaimed on purchases' },
  { key: 'box5', label: 'Box 5', desc: 'Net VAT payable / reclaimable', derived: true, highlight: 'green' },
  { key: 'box6', label: 'Box 6', desc: 'Total sales ex-VAT' },
  { key: 'box7', label: 'Box 7', desc: 'Total purchases ex-VAT' },
  { key: 'box8', label: 'Box 8', desc: 'Total EC supplies ex-VAT' },
  { key: 'box9', label: 'Box 9', desc: 'Total EC acquisitions ex-VAT' },
]

const fmt = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export default function VATReturn() {
  const [tab, setTab] = useState('upload')          // 'upload' | 'manual'
  const [scheme, setScheme] = useState('standard')
  const [frsRate, setFrsRate] = useState('')
  const [clientName, setClientName] = useState('')
  const [period, setPeriod] = useState('')

  // Bank statement upload state
  const [file, setFile] = useState(null)
  const [rawText, setRawText] = useState('')
  const fileRef = useRef()

  // Manual entry state
  const [manual, setManual] = useState({
    box1_vat_due_sales: '', box2_vat_due_acquisitions: '',
    box4_vat_reclaimed: '', box6_total_sales_ex_vat: '',
    box7_total_purchases_ex_vat: '', box8_total_supplies_ex_vat: '',
    box9_total_acquisitions_ex_vat: ''
  })

  // Result state
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // ── Parse CSV/Excel text into transaction array ──────────────────────────
  function parseTransactions(text) {
    const lines = text.trim().split('\n').filter(l => l.trim())
    const txns = []
    for (const line of lines.slice(1)) {  // skip header
      const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g,''))
      if (cols.length < 3) continue
      const [date, desc, ...rest] = cols
      // Try to find amount and direction
      const amtStr = rest.find(c => /[\d,.]+/.test(c)) || '0'
      const amt = parseFloat(amtStr.replace(/[^0-9.-]/g,'')) || 0
      // Heuristic: if there's a debit and credit column
      const hasCredit = rest.length >= 2 && parseFloat((rest[1]||'').replace(/[^0-9.-]/g,'')) > 0
      txns.push({
        date: date || '',
        description: desc || '',
        amount: Math.abs(amt),
        direction: hasCredit ? 'in' : 'out'
      })
    }
    return txns
  }

  async function handleFileChange(e) {
    const f = e.target.files[0]
    if (!f) return
    setFile(f)
    const text = await f.text()
    setRawText(text)
  }

  async function handleUploadSubmit() {
    if (!rawText) { setError('Please upload a CSV file first'); return }
    setLoading(true); setError(''); setResult(null)
    try {
      const transactions = parseTransactions(rawText)
      if (transactions.length === 0) {
        setError('Could not parse transactions. Make sure it is a CSV with date, description, amount columns.')
        setLoading(false); return
      }
      const res = await fetch(`${API}/api/vat/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: clientName || 'Client',
          period: period || 'Q1 2025',
          vat_scheme: scheme,
          flat_rate_percent: parseFloat(frsRate) || 0,
          transactions
        })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Server error')
      setResult(data)
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }

  async function handleManualSubmit() {
    setLoading(true); setError(''); setResult(null)
    try {
      const res = await fetch(`${API}/api/vat/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: clientName || 'Client',
          period: period || 'Q1 2025',
          vat_scheme: scheme,
          flat_rate_percent: parseFloat(frsRate) || 0,
          ...Object.fromEntries(
            Object.entries(manual).map(([k, v]) => [k, parseFloat(v) || 0])
          )
        })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Server error')
      setResult(data)
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }

  async function handleExport() {
    if (!result) return
    try {
      const res = await fetch(`${API}/api/vat/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(result)
      })
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `VAT_${(result.client_name||'Client').replace(/ /g,'_')}_${(result.period||'').replace(/ /g,'_')}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError('Export error: ' + e.message)
    }
  }

  // ── Styles ────────────────────────────────────────────────────────────────
  const s = {
    page: { padding: '24px', maxWidth: 900, margin: '0 auto', fontFamily: 'Calibri, sans-serif' },
    card: { background: '#0f1923', border: '1px solid rgba(201,151,58,0.15)', borderRadius: 10, padding: 24, marginBottom: 20 },
    row: { display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' },
    label: { fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 },
    input: { background: '#0B1929', border: '1px solid rgba(201,151,58,0.2)', borderRadius: 6, color: '#fff', padding: '9px 12px', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' },
    tabBar: { display: 'flex', gap: 2, marginBottom: 20, background: '#0B1929', borderRadius: 8, padding: 4 },
    tab: (active) => ({ flex: 1, padding: '9px 0', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: active ? '#C9973A' : 'transparent', color: active ? '#0B1929' : 'rgba(255,255,255,0.4)', transition: 'all .2s' }),
    btn: (variant='primary') => ({
      padding: '11px 26px', borderRadius: 7, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13,
      background: variant === 'primary' ? '#C9973A' : variant === 'export' ? '#1a4a2e' : '#1a1a2e',
      color: variant === 'primary' ? '#0B1929' : variant === 'export' ? '#4ade80' : '#fff'
    }),
    sectionTitle: { fontSize: 11, fontWeight: 700, color: 'rgba(201,151,58,0.7)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 },
    boxRow: (highlight) => ({
      display: 'flex', alignItems: 'center', padding: '12px 16px', borderRadius: 7, marginBottom: 6,
      background: highlight === 'blue' ? 'rgba(59,130,246,0.08)' : highlight === 'green' ? 'rgba(74,222,128,0.08)' : 'rgba(255,255,255,0.03)',
      border: `1px solid ${highlight === 'blue' ? 'rgba(59,130,246,0.2)' : highlight === 'green' ? 'rgba(74,222,128,0.2)' : 'rgba(255,255,255,0.06)'}`
    }),
    boxLabel: { width: 54, fontSize: 12, fontWeight: 700, color: '#C9973A', flexShrink: 0 },
    boxDesc: { flex: 1, fontSize: 12, color: 'rgba(255,255,255,0.55)' },
    boxAmt: (highlight) => ({ fontSize: 15, fontWeight: 700, color: highlight === 'green' ? '#4ade80' : highlight === 'blue' ? '#93c5fd' : '#fff', minWidth: 120, textAlign: 'right' }),
    flagRow: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 12px', background: 'rgba(255,170,0,0.06)', borderRadius: 6, marginBottom: 6, border: '1px solid rgba(255,170,0,0.15)' },
    summaryBox: { padding: '12px 16px', background: 'rgba(201,151,58,0.07)', borderRadius: 7, border: '1px solid rgba(201,151,58,0.15)', marginBottom: 16, fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }
  }

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#fff', margin: 0 }}>VAT Return Builder</h1>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: '4px 0 0' }}>Standard & Flat Rate Scheme · Box 1–9 · Excel Export</p>
      </div>

      {/* Client + Period + Scheme */}
      <div style={s.card}>
        <div style={s.sectionTitle}>Return Details</div>
        <div style={s.row}>
          <div style={{ flex: 1, minWidth: 160 }}>
            <label style={s.label}>Client Name</label>
            <input style={s.input} value={clientName} onChange={e => setClientName(e.target.value)} placeholder="e.g. Acme Ltd" />
          </div>
          <div style={{ flex: 1, minWidth: 140 }}>
            <label style={s.label}>VAT Period</label>
            <input style={s.input} value={period} onChange={e => setPeriod(e.target.value)} placeholder="e.g. Q1 Jan–Mar 2025" />
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <label style={s.label}>VAT Scheme</label>
            <select style={s.input} value={scheme} onChange={e => setScheme(e.target.value)}>
              <option value="standard">Standard Rate (20%)</option>
              <option value="flat_rate">Flat Rate Scheme</option>
            </select>
          </div>
          {scheme === 'flat_rate' && (
            <div style={{ flex: 1, minWidth: 120 }}>
              <label style={s.label}>FRS Rate (%)</label>
              <input style={s.input} type="number" value={frsRate} onChange={e => setFrsRate(e.target.value)} placeholder="e.g. 12.5" />
            </div>
          )}
        </div>
      </div>

      {/* Tab selector */}
      <div style={s.tabBar}>
        <button style={s.tab(tab==='upload')} onClick={() => setTab('upload')}>📄 Bank Statement Upload</button>
        <button style={s.tab(tab==='manual')} onClick={() => setTab('manual')}>✏️ Manual Entry</button>
      </div>

      {/* Upload tab */}
      {tab === 'upload' && (
        <div style={s.card}>
          <div style={s.sectionTitle}>Upload Bank Statement (CSV)</div>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginBottom: 16 }}>
            GPT-4o will read the transactions and extract all 9 VAT boxes automatically.
            CSV should have columns: date, description, amount (debit/credit).
          </p>
          <div
            style={{ border: '2px dashed rgba(201,151,58,0.25)', borderRadius: 8, padding: '32px 20px', textAlign: 'center', cursor: 'pointer', marginBottom: 16 }}
            onClick={() => fileRef.current.click()}
          >
            <div style={{ fontSize: 28, marginBottom: 8 }}>📂</div>
            <div style={{ fontSize: 13, color: file ? '#C9973A' : 'rgba(255,255,255,0.35)' }}>
              {file ? `✓ ${file.name}` : 'Click to upload CSV'}
            </div>
            <input ref={fileRef} type="file" accept=".csv,.txt" style={{ display: 'none' }} onChange={handleFileChange} />
          </div>
          <button style={s.btn('primary')} onClick={handleUploadSubmit} disabled={loading}>
            {loading ? '⏳ Analysing with GPT-4o…' : '▶ Analyse & Generate VAT Return'}
          </button>
        </div>
      )}

      {/* Manual tab */}
      {tab === 'manual' && (
        <div style={s.card}>
          <div style={s.sectionTitle}>Manual Entry — Enter your figures</div>
          {[
            ['box1_vat_due_sales',            'Box 1 — VAT due on sales (£)'],
            ['box2_vat_due_acquisitions',      'Box 2 — VAT due on EC acquisitions (£)'],
            ['box4_vat_reclaimed',             'Box 4 — VAT reclaimed on purchases (£)'],
            ['box6_total_sales_ex_vat',        'Box 6 — Total sales ex-VAT (£)'],
            ['box7_total_purchases_ex_vat',    'Box 7 — Total purchases ex-VAT (£)'],
            ['box8_total_supplies_ex_vat',     'Box 8 — EC supplies ex-VAT (£)'],
            ['box9_total_acquisitions_ex_vat', 'Box 9 — EC acquisitions ex-VAT (£)'],
          ].map(([key, lbl]) => (
            <div key={key} style={{ marginBottom: 12 }}>
              <label style={s.label}>{lbl}</label>
              <input
                style={{ ...s.input, maxWidth: 240 }}
                type="number"
                value={manual[key]}
                onChange={e => setManual(m => ({ ...m, [key]: e.target.value }))}
                placeholder="0.00"
              />
            </div>
          ))}
          <div style={{ marginTop: 16 }}>
            <button style={s.btn('primary')} onClick={handleManualSubmit} disabled={loading}>
              {loading ? '⏳ Computing…' : '▶ Compute VAT Return'}
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#f87171', fontSize: 13 }}>
          ⚠ {error}
        </div>
      )}

      {/* Results */}
      {result && (
        <div style={s.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{result.client_name} — {result.period}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>
                {result.vat_scheme === 'flat_rate' ? `Flat Rate Scheme (${result.flat_rate_percent}%)` : 'Standard VAT Scheme (20%)'}
                {result.source === 'bank_statement' && ` · ${result.transactions_processed} transactions processed`}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={s.btn('export')} onClick={handleExport}>⬇ Excel</button>
            </div>
          </div>

          {/* GPT summary */}
          {result.summary && (
            <div style={s.summaryBox}>
              <span style={{ color: '#C9973A', fontWeight: 700 }}>GPT Summary: </span>{result.summary}
            </div>
          )}

          {/* Box 1-9 */}
          <div style={s.sectionTitle}>VAT Return — Box 1 to 9</div>
          {BOX_META.map(({ key, label, desc, highlight }) => (
            <div key={key} style={s.boxRow(highlight)}>
              <div style={s.boxLabel}>{label}</div>
              <div style={s.boxDesc}>{desc}</div>
              <div style={s.boxAmt(highlight)}>{fmt(result.boxes[key])}</div>
            </div>
          ))}

          {/* Net position callout */}
          <div style={{ marginTop: 16, padding: '14px 18px', borderRadius: 8, background: result.boxes.is_repayment ? 'rgba(220,38,38,0.08)' : 'rgba(74,222,128,0.08)', border: `1px solid ${result.boxes.is_repayment ? 'rgba(220,38,38,0.3)' : 'rgba(74,222,128,0.3)'}` }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: result.boxes.is_repayment ? '#f87171' : '#4ade80' }}>
              {result.boxes.is_repayment ? '↩ VAT Repayment Due from HMRC' : '→ VAT Payable to HMRC'}
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#fff', marginTop: 4 }}>
              {fmt(Math.abs(result.boxes.box5))}
            </div>
          </div>

          {/* Flags */}
          {result.flags && result.flags.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={s.sectionTitle}>Review Flags</div>
              {result.flags.map((flag, i) => (
                <div key={i} style={s.flagRow}>
                  <span style={{ color: '#FBBF24', fontSize: 13 }}>⚠</span>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>{flag}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
